import Ticket from "@/types/Ticket";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/atoms/Accordion";
import { useState, useRef } from "react";
import Button from "@/components/atoms/Button";
import Chat from "@/components/molecules/Chat";
import Reply from "@/types/Reply";
import { SanitizedContent } from "@/hooks/useSanitize";
import useAccess from "@/hooks/useAccess";
import { Paperclip } from "lucide-react";
import Attachment from "@/types/Attachment";
import axios from "axios";

type Props = {
  ticket: Ticket;
  replies: Reply[];
  setSelectedTicketStatus: (status: string) => void;
};

const Detail = ({ ticket, replies, setSelectedTicketStatus }: Props) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isChatVisible, setIsChatVisible] = useState(false);
  const chatRef = useRef<HTMLDivElement | null>(null);
  const { currentUser } = useAccess();
  const lastReply = ticket.last_reply;

  const handleTicketInfoToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  const DetailItem = ({ label, value }: { label: string; value: string }) => (
    <div className="text-left">
      <h4 className="text-sm font-light block">{label}</h4>
      <p className="font-bold block">{value}</p>
    </div>
  );

  const handleSeeChat = () => {
    setIsChatVisible(true);
    setTimeout(() => {
      chatRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 0);
  };

  const formatDate = (input: string): string => {
    const date = new Date(input.replace(/(\d+)\/(\d+)\/(\d+)/, '$2/$1/$3'));

    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };

    return date.toLocaleDateString('en-GB', options).replace(',', '');
  };

  const getCsrfToken = () => {
    const tokenMeta = document.querySelector('meta[name="csrf-token"]');
    return tokenMeta ? tokenMeta.getAttribute("content") || "" : "";
  };

  const handleAttachmentClick = (attachment : Attachment) => async (e: React.MouseEvent) => {
    e.preventDefault();
    const csrfToken = getCsrfToken();

    const response = await axios.post('/attachments/download_url', {
      file_name: attachment.name,
      file_type: attachment.content_type,
      folder_name: `Tickets/${ticket.display_id}`,
    }, {
      headers: {
        'X-CSRF-Token': csrfToken,
      },
    });

    const presignedUrl: string = response.data.url;

    window.open(presignedUrl, '_blank');
  }


  return (
    <div>

      <div className="pb-8">
        <div className="w-full mx-auto p-4 shadow-md rounded-lg border border-border">
          <Accordion
            className="w-full rounded-md"
            type="single"
            defaultValue="item-1"
            collapsible
          >
            <AccordionItem value="item-1">
              <div className="relative w-full">
                <AccordionTrigger
                  onClick={handleTicketInfoToggle}
                >
                  Ticket Details
                </AccordionTrigger>
                <AccordionContent className="w-full px-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-6 gap-x-8">
                    <DetailItem
                      label="Account"
                      value={ticket?.account?.name || "-"}
                    />
                    <DetailItem
                      label="Requester"
                      value={ticket?.requester?.name || "-"}
                    />
                    <DetailItem
                      label="Created by"
                      value={ticket?.created_by?.name || "-"}
                    />
                    <DetailItem
                      label="Internal Owner"
                      value={ticket?.internal_owner?.name || "-"}
                    />
                    <DetailItem
                      label="Priority"
                      value={ticket?.priority?.name || "-"}
                    />
                    <DetailItem
                      label="Ticket Type"
                      value={ticket?.ticket_type?.name || "-"}
                    />

                    {currentUser && !currentUser?.roles?.some((role) => role.name === "client") && (
                      <>
                        <DetailItem
                          label="Internal Ticket Type(s)"
                          value={
                            ticket?.internal_ticket_types
                              ?.map((item) => item.internal_ticket_type?.name)
                              .join(", ") || "-"
                          }
                        />
                        <DetailItem
                          label="Internal Sub Ticket Type(s)"
                          value={
                            ticket?.internal_ticket_types
                              ?.map((item) => item.internal_sub_ticket_type?.name)
                              .join(", ") || "-"
                          }
                        />

                        <DetailItem
                          label="Sow"
                          value={ticket?.sow?.name || "-"}
                        />
                      </>
                    )}
                  </div>
                </AccordionContent>
              </div>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
      <div>
      <div className="w-full mx-auto p-4 shadow-md rounded-lg border border-border">
        <div className="w-full pl-6 pt-4">
          <span className="text-xl font-bold">Description</span>
        </div>
        <div className="w-full p-6">
          <div className="flex justify-start mb-4">
            <div className="w-5/6 p-4 left">
              <div className="justify-start">
                <div className="flex space-x-4">
                  <img
                    src={
                      "avatar_url" in ticket.created_by
                        ? ticket.created_by.avatar_url
                        : "/default-avatar.png"
                    }
                    alt="Profile"
                    className="w-12 h-12 rounded-full dark:bg-darkTextColor"
                  />
                  <div>
                    <div className="flex flex-col">
                      <p className="text-md font-semibold">
                        {ticket.created_by?.name}
                      </p>
                      <span className="text-xs text-gray-500 dark:text-darkTextColor">
                        {formatDate(ticket.created_at)}
                      </span>
                    </div>

                  </div>
                </div>
              </div>
              <div className="mt-4">
                <SanitizedContent html={ticket.description} />
              </div>
                {ticket.attachments && ticket.attachments.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-semibold">Attachments:</p>
                    <ul className="mt-2">
                      {ticket?.attachments?.map((attachment) => (
                        <div key={attachment.id} className="flex">
                          <Paperclip size={24} className="text-blue-500 mr-2" />
                          <a
                            href="#"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-bold text-blue-500 hover:underline block"
                            onClick={handleAttachmentClick(attachment)}
                          >
                            {attachment.original_name || attachment.name}
                          </a>
                        </div>
                      ))}
                    </ul>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>

      </div>
      <div className="mt-10 border border-border">
        {!isChatVisible ? (
          <div className="w-full mx-auto p-4 shadow-md rounded-lg">
            <div className="w-full pl-6 pt-4">
              <span className="text-xl font-bold">Chat</span>
            </div>
            <div className="w-full p-6">
              <div className="flex justify-start mb-4">
                <div className="w-5/6 p-4 left rounded-lg">
                  {lastReply && (
                    <div className="justify-start">
                    <div className="flex space-x-4">
                      <img
                        src={
                          "avatar_url" in lastReply.user
                            ? lastReply.user.avatar_url
                            : "/default-avatar.png"
                        }
                        alt="Profile"
                        className="w-12 h-12 rounded-full dark:bg-darkTextColor"
                      />
                      <div>
                        <div className="flex flex-col">
                          <p className="text-md font-semibold">
                            {lastReply.user?.name}
                          </p>
                          <span className="text-xs text-gray-500 dark:text-darkTextColor">
                            {formatDate(lastReply.created_at)}
                          </span>
                        </div>

                      </div>
                    </div>
                  </div>
                  )}
                  
                  <div className="mt-4">
                    {lastReply ? (
                      <SanitizedContent html={lastReply.message} />
                    ) : (
                      <div className="flex items-center justify-center">No messages sent</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-4">
                <Button
                  className="inline-flex items-center gap-2 text-black"
                  onClick={handleSeeChat}
                >
                  <span>{lastReply ? "See Chat" : "Start Messaging"}</span>
                </Button>
              </div>
            </div>
          </div>

        ) : (
          <div ref={chatRef}><Chat replies={replies} ticket={ticket} setSelectedTicketStatus={setSelectedTicketStatus}/></div>

        )}
      </div>
    </div>
  );
};

export default Detail;
